import styled, { css, keyframes } from 'styled-components';

/** Holds the button, its badge and the panel, which hangs off its corner. */
export const BellAnchor = styled.div`
  position: relative;
  flex: none;
`;

/**
 * A small swing when a new one arrives - the badge alone changes a digit,
 * which is easy to miss from across the screen.
 */
const ring = keyframes`
  0% { transform: rotate(0); }
  20% { transform: rotate(14deg); }
  40% { transform: rotate(-12deg); }
  60% { transform: rotate(8deg); }
  80% { transform: rotate(-4deg); }
  100% { transform: rotate(0); }
`;

export const BellGlyph = styled.span<{ $ringing: boolean }>`
  display: flex;
  transform-origin: 50% 10%;

  ${({ $ringing }) =>
    $ringing &&
    css`
      animation: ${ring} 0.8s ease;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * The unread count, on the button's top right corner. Above the open
 * dropdown's overlay (9) and the opened button itself (10), so the number
 * stays readable while the panel is open.
 */
export const CountBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 11;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  border: 2px solid ${({ theme }) => theme.white};
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  font-size: 11px;
  line-height: 16px;
  font-weight: 700;
  text-align: center;
  pointer-events: none;
`;

/**
 * The panel inside the dropdown: a fixed header and footer around a list that
 * scrolls on its own, so Mark all and See all never scroll away.
 */
export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 380px;
  max-width: calc(100vw - 32px);
  max-height: min(560px, calc(100vh - 140px));
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px 8px;
`;

export const PanelTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.darkGray};
`;

export const PanelList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* Reaching the end of the list must not start scrolling the page under the
     overlay - that carries the whole panel away with the header. */
  overscroll-behavior: contain;
  padding: 0 8px 8px;
  -webkit-overflow-scrolling: touch;
`;

/** Empty, failed - one short sentence where the rows would be. */
export const PanelNote = styled.div`
  padding: 32px 20px;
  text-align: center;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.lightGray};
`;

export const PanelFooter = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px 20px 12px;
  border-top: ${({ theme }) => theme.borders.border1};
`;
